/**
 * ustvaDeadline — purer Helper für die Cockpit-Steuer-Countdown-Kachel
 * (M-UX-4.2 §2, Kachel #7). Kein Firestore, kein React — trivial unit-testbar.
 *
 * Regel v1 (§18 UStG, monatlicher Melderhythmus, bewusste Vereinfachung):
 * die nächste USt-Voranmeldungs-Frist ist IMMER der 10. des auf `todayISO`
 * folgenden Monats — unabhängig davon, ob der heutige Tag vor oder nach dem
 * 10. des laufenden Monats liegt. Dauerfristverlängerung, vierteljährlicher/
 * jährlicher Rhythmus und eine Verschiebung bei Wochenende/Feiertag werden
 * v1 bewusst NICHT berücksichtigt (neutrale Erinnerung, kein verbindliches
 * Datum — s. Jurist-freigegebener Disclaimer-Text, `cockpit.tax.disclaimer`).
 *
 * Kanonische I/O-Paare (Testvertrag, `plan-ux-4.2-cockpit.md` §2):
 *   - `computeNextUstvaDeadline('2026-07-09')` → `{deadlineISO:'2026-08-10', daysRemaining:32}`
 *   - `computeNextUstvaDeadline('2026-12-15')` → `deadlineISO==='2027-01-10'` (Jahreswechsel)
 *   - Kaputtes `todayISO` ⇒ wirft NICHT, liefert einen neutralen Zustand
 *     (`deadlineISO: null, daysRemaining: null`) — die Kachel zeigt dann „—".
 *
 * @module utils/ustvaDeadline
 */

/** Ergebnis von {@link computeNextUstvaDeadline}. */
export interface UstvaDeadlineResult {
  /** ISO `YYYY-MM-DD` der nächsten Frist, oder `null` bei kaputtem Input. */
  deadlineISO: string | null;
  /** Ganzzahlige Tage bis zur Frist (>= 0 im Regelfall), oder `null` bei kaputtem Input. */
  daysRemaining: number | null;
}

/** Zwei-stellige Null-gepaddete Zahl als String. */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Validiert `YYYY-MM-DD` streng genug, um NaN/Garbage-Folgefehler zu
 * vermeiden — ohne volle Kalender-Bibliothek. Analog `cockpitLoaders.ts`
 * `isValidISODate` (bewusst dupliziert, kein Cross-Import in dieses pure
 * Utility-Modul).
 */
function isValidISODate(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  // Reale Kalender-Gültigkeit (z. B. 2026-02-30 wäre sonst formal "gültig").
  const d = new Date(Date.UTC(year, month - 1, day));
  return (
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day
  );
}

/**
 * Berechnet die nächste USt-Voranmeldungs-Frist (10. des Folgemonats von
 * `todayISO`) rein aus Kalenderarithmetik (UTC, keine Zeitzonen-Falle).
 *
 * @param todayISO - Referenzdatum `YYYY-MM-DD` (i. d. R. heute).
 * @returns `{deadlineISO, daysRemaining}` — beide `null`, wenn `todayISO`
 *   kein valides `YYYY-MM-DD`-Datum ist (fail-closed neutral, wirft NICHT).
 */
export function computeNextUstvaDeadline(todayISO: string): UstvaDeadlineResult {
  if (!isValidISODate(todayISO)) {
    return { deadlineISO: null, daysRemaining: null };
  }

  const year = Number(todayISO.slice(0, 4));
  const month = Number(todayISO.slice(5, 7)); // 1..12

  // "Folgemonat" — bei Dezember rollt das Jahr.
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextMonthYear = month === 12 ? year + 1 : year;

  const deadlineISO = `${nextMonthYear}-${pad2(nextMonth)}-10`;

  const todayUTC = Date.UTC(year, month - 1, Number(todayISO.slice(8, 10)));
  const deadlineUTC = Date.UTC(nextMonthYear, nextMonth - 1, 10);
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysRemaining = Math.round((deadlineUTC - todayUTC) / msPerDay);

  return { deadlineISO, daysRemaining };
}
